/**
 * DateTimePicker - Combined DatePicker + TimePicker component
 *
 * Provides a combined date and time selection UI with:
 * - DatePicker for date selection
 * - TimePicker with freeform time input
 * - Validation for future time selection
 * - Invalid time format handling
 */

import { DatePicker } from '@fluentui/react-datepicker-compat';
import { TimePicker } from '@fluentui/react-timepicker-compat';
import type { TimeSelectionData, TimeSelectionEvents } from '@fluentui/react-timepicker-compat';
import { useState, useMemo, useCallback } from 'react';
import { useDateTimePickerStyles } from './DateTimePicker.styles';

/**
 * Formats a Date to a time string matching the TimePicker's format (e.g., "9:00 AM").
 * Uses 12-hour format to match hourCycle="h12" on the TimePicker.
 */
const formatTimeForPicker = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${period}`;
};

export interface DateTimePickerProps {
  /** Called when a valid date/time is selected */
  onSelect: (selectedTime: string) => void;
  /** Called to set or clear validation errors */
  setError: (error: string | undefined) => void;
  /** Optional initial value as ISO string */
  initialValue?: string | undefined;
  /** Whether the picker is disabled */
  disabled?: boolean | undefined;
  /** Optional minimum date (defaults to today) */
  minDate?: Date | undefined;
}

/**
 * Combined DatePicker + TimePicker with freeform time input and validation.
 * Validates that selected time is in the future and handles invalid time formats.
 */
export const DateTimePicker = ({
  onSelect,
  setError,
  initialValue,
  disabled = false,
  minDate: minDateProp,
}: DateTimePickerProps): JSX.Element => {
  const styles = useDateTimePickerStyles();

  const minDate = useMemo(() => minDateProp ?? new Date(), [minDateProp]);
  const [selectedDate, setSelectedDate] = useState<Date | null | undefined>(
    initialValue ? new Date(initialValue) : null
  );
  const [selectedTime, setSelectedTime] = useState<Date | null>(
    initialValue ? new Date(initialValue) : null
  );
  const [timePickerValue, setTimePickerValue] = useState<string>(
    selectedTime ? formatTimeForPicker(selectedTime) : ''
  );

  const handleDateTimeSelection = useCallback(
    (date: Date | null | undefined, time: Date | null | undefined) => {
      if (date && time) {
        const newDateTime = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          time.getHours(),
          time.getMinutes()
        );

        const today = new Date();

        if (
          today.getDate() === date.getDate() &&
          today.getMonth() === date.getMonth() &&
          today.getFullYear() === date.getFullYear() &&
          today.getTime() > newDateTime.getTime()
        ) {
          setError('Please select a future time.');
          return;
        }

        setError(undefined);
        onSelect(newDateTime.toISOString());
      } else if (date) {
        setError(undefined);
        onSelect(date.toISOString());
      }
    },
    [onSelect, setError]
  );

  const handleInvalidTimeSelection = useCallback(() => {
    setSelectedTime(null);
    handleDateTimeSelection(selectedDate, null);
    setError('Please enter a valid time format (e.g., 2:30 PM).');
  }, [handleDateTimeSelection, selectedDate, setError]);

  const onSelectDate = useCallback(
    (date: Date | null | undefined) => {
      setSelectedDate(date);
      handleDateTimeSelection(date, selectedTime);
    },
    [handleDateTimeSelection, selectedTime]
  );

  const onTimeChange = useCallback(
    (_: TimeSelectionEvents, data: TimeSelectionData) => {
      setSelectedTime(data.selectedTime ?? null);
      setTimePickerValue(data.selectedTimeText ?? '');

      if (selectedDate && data.selectedTime) {
        handleDateTimeSelection(selectedDate, data.selectedTime);
      }
    },
    [handleDateTimeSelection, selectedDate]
  );

  const onTimePickerInput = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      setTimePickerValue(ev.target.value);
    },
    []
  );

  const onTimePickerBlur = useCallback(() => {
    if (timePickerValue) {
      const normalizedTime = timePickerValue.toLowerCase().replace(/\s+/g, '');
      const morningTime = normalizedTime.endsWith('am')
        ? normalizedTime.replace('am', '')
        : undefined;
      const eveningTime = normalizedTime.endsWith('pm')
        ? normalizedTime.replace('pm', '')
        : undefined;
      const timeParts = morningTime
        ? morningTime.split(':')
        : eveningTime
          ? eveningTime.split(':')
          : [];

      if (timeParts.length === 0) {
        return handleInvalidTimeSelection();
      }

      let hours = Number.parseInt(timeParts[0] ?? '');
      const minutes =
        timeParts.length > 1 ? Number.parseInt(timeParts[1] ?? '') : 0;

      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 1 ||
        hours > 12 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return handleInvalidTimeSelection();
      }

      if (eveningTime && hours < 12) {
        hours += 12;
      } else if (morningTime && hours === 12) {
        hours = 0;
      }

      const newTime = new Date(new Date().setHours(hours, minutes));
      setSelectedTime(newTime);

      if (selectedDate && newTime) {
        handleDateTimeSelection(selectedDate, newTime);
      }
    }
  }, [handleDateTimeSelection, handleInvalidTimeSelection, selectedDate, timePickerValue]);

  return (
    <div className={styles.dateTimeRow}>
      <DatePicker
        placeholder="Select a date..."
        value={selectedDate ?? null}
        inlinePopup={true}
        minDate={minDate}
        onSelectDate={onSelectDate}
        disabled={disabled}
        aria-label='Date'
      />
      <TimePicker
        className={styles.timePicker}
        placeholder="Select a time..."
        {...(selectedDate ? { dateAnchor: selectedDate } : {})}
        selectedTime={selectedTime}
        onTimeChange={onTimeChange}
        value={timePickerValue}
        inlinePopup={true}
        freeform={true}
        onInput={onTimePickerInput}
        onBlur={onTimePickerBlur}
        disabled={disabled}
        aria-label='Time'
      />
    </div>
  );
};

DateTimePicker.displayName = 'DateTimePicker';
